import React from 'react';
import { ControlRow } from '../../shared/ControlRow';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import type { ExtendedTier } from '@/types/ExtendedTier';
import { DURATION_MEDIUM } from '@/lib/motion';
import { AnimatedCollapse } from '@/components/ui/animated-collapse';
import { ExtendedOvertimeSection } from '../../shared/ExtendedOvertimeSection';

/* ------------------------------------------------------------------ */
/*  Scope Bar                                                          */
/* ------------------------------------------------------------------ */

export function ScopeBar({
  shiftHours,
  violationHours,
  isShortedHours,
}: {
  shiftHours: number;
  violationHours: number;
  isShortedHours: boolean;
}) {
  const surchargedHours = isShortedHours ? violationHours : shiftHours;
  const normalHours = isShortedHours ? shiftHours - violationHours : 0;
  const boundaryPct = shiftHours > 0 ? (violationHours / shiftHours) * 100 : 0;

  return (
    <div className="mt-1 rounded-[0.25rem] border border-primary/45 bg-primary/[0.04] shadow-[0_0_8px_color-mix(in_oklab,var(--primary),transparent_90%)] [border-top-color:color-mix(in_oklab,var(--primary),transparent_65%)] px-3 pt-4 pb-1">
      {/* Shift label + scope bar inline */}
      <div className="flex items-start gap-2">
        <span className="inline-flex h-6 items-center font-heading text-[11px] sm:text-[12px] leading-none uppercase tracking-[1px] text-foreground/90 shrink-0">Shift</span>
        <div className="flex-1 min-w-0">
          <div className="relative">
            <div className="h-6 rounded-[0.25rem] overflow-hidden flex border border-primary/20">
              <div className="flex items-center justify-center motion-safe:transition-[flex] motion-safe:duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] bg-primary/[0.55] overflow-hidden" style={{ flex: surchargedHours }}>
                <span className="font-mono font-semibold whitespace-nowrap text-[11px] text-foreground/90">
                  Surcharge
                </span>
              </div>
              <div className="flex items-center justify-center motion-safe:transition-[flex] motion-safe:duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] bg-foreground/10 overflow-hidden" style={{ flex: normalHours }}>
                {normalHours > 0 && (
                  <span className="font-mono whitespace-nowrap text-[11px] text-muted-foreground/55">
                    No Surcharge
                  </span>
                )}
              </div>
            </div>
            <div
              className="absolute top-1 bottom-1 w-0.5 bg-primary"
              style={{ left: `calc(${boundaryPct}% + 1px)`, opacity: isShortedHours ? 1 : 0.4 }}
            />
          </div>
          {/* Scope bar labels — inside the flex-1 so they align with the bar */}
          <div className="relative mt-1 overflow-hidden" style={{ height: 16 }}>
            <span className="absolute font-mono whitespace-nowrap text-center text-[11px] leading-none text-primary/70" style={{ left: `${boundaryPct}%`, transform: 'translateX(-50%)', top: 2 }}>
              Shorted Hours
            </span>
            <span className="absolute right-0 font-mono text-muted-foreground/50 text-[11px] leading-none" style={{ top: 2 }}>shift end</span>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Method Control Panel                                               */
/* ------------------------------------------------------------------ */

export interface MethodControlPanelProps {
  extendedOvertimeEnabled: boolean;
  onExtendedOvertimeChange: (enabled: boolean) => void;
  extendedOvertimeTiers?: ExtendedTier[];
  highestOvertimeRate?: number;
  highestOvertimeTierName?: string;
  showExtendedOvertimeControl?: boolean;
  editableExtendedTiers?: ExtendedTier[];
  onEditableExtendedTiersChange?: (tiers: ExtendedTier[]) => void;
  editableExtendedTiersContext?: 'user' | 'client';
  editableExtendedTiersMax?: number;
  /** Optional leading control row (e.g. Guaranteed Rate selector) */
  leadingControl?: React.ReactNode;
}

export function MethodControlPanel({
  extendedOvertimeEnabled,
  onExtendedOvertimeChange,
  extendedOvertimeTiers = [],
  highestOvertimeRate,
  highestOvertimeTierName,
  showExtendedOvertimeControl = true,
  editableExtendedTiers,
  onEditableExtendedTiersChange,
  editableExtendedTiersContext,
  editableExtendedTiersMax,
  leadingControl,
}: MethodControlPanelProps) {
  const sortedExtendedTiers = [...extendedOvertimeTiers].sort((a, b) => a.sortOrder - b.sortOrder);
  const hasEditableTierBinding = Array.isArray(editableExtendedTiers) && Boolean(onEditableExtendedTiersChange);
  const hasValidHighestRate = Number.isFinite(highestOvertimeRate);

  return (
    <div className="mt-3 rounded-[0.25rem] border border-border/30 bg-surface-2/30 divide-y divide-border/20">
      {leadingControl}
      {showExtendedOvertimeControl && (
        <>
          <ControlRow label="Extended Overtime" description="Add tiers beyond your standard overtime ladder for stronger short-turn surcharges.">
            <Switch
              aria-label="Toggle extended overtime"
              checked={extendedOvertimeEnabled}
              onCheckedChange={onExtendedOvertimeChange}
            />
          </ControlRow>
          <AnimatedCollapse
            open={extendedOvertimeEnabled}
            duration={Math.round(DURATION_MEDIUM * 1000)}
          >
            <div
              className="overflow-hidden"
              data-anim-audit="short-turn-method-control-extended-tier-list-collapse"
            >
              <div className="px-2 py-2" data-anim-audit="short-turn-method-control-extended-tier-list-shell">
                {hasEditableTierBinding ? (
                  hasValidHighestRate ? (
                    <ExtendedOvertimeSection
                      mode="editor-only"
                      enabled={true}
                      onEnabledChange={() => {}}
                      tiers={editableExtendedTiers ?? []}
                      onTiersChange={onEditableExtendedTiersChange ?? (() => {})}
                      highestOvertimeRate={highestOvertimeRate as number}
                      highestOvertimeTierName={highestOvertimeTierName}
                      context={editableExtendedTiersContext ?? 'user'}
                      maxTiers={editableExtendedTiersMax}
                    />
                  ) : (
                    <div
                      className="rounded-lg border border-border/60 bg-surface-1/30 px-3 py-3"
                      data-anim-audit="short-turn-method-control-extended-tier-editor-unavailable"
                    >
                      <p className="text-xs font-mono text-muted-foreground italic text-center">
                        Extended overtime editing is unavailable until your overtime ladder has a valid top rate.
                      </p>
                    </div>
                  )
                ) : (
                  <div className="rounded-lg border border-border/60 bg-surface-1/30 p-2 sm:p-2 space-y-2" data-anim-audit="short-turn-method-control-extended-tier-list">
                    <p className="font-heading text-[11px] sm:text-[12px] uppercase tracking-wider text-primary-text/80 text-center">
                      Extended Surcharge Ladder
                    </p>

                    <div className="space-y-2">
                      {highestOvertimeRate != null && highestOvertimeTierName && (
                        <div
                          className={cn(
                            'grid grid-cols-[56px_1fr_auto] items-center gap-2 px-3 py-2',
                            'rounded-lg border border-dashed border-border/40 bg-transparent opacity-45'
                          )}
                          aria-hidden="true"
                        >
                          <span className="font-mono text-sm font-bold tabular-nums text-muted-foreground">
                            {highestOvertimeRate}x
                          </span>
                          <span className="font-heading text-[13px] uppercase tracking-wide truncate text-muted-foreground">
                            {highestOvertimeTierName}
                          </span>
                          <span className="inline-flex items-center rounded-lg px-2 py-1 text-[11px] sm:text-[12px] font-mono font-semibold leading-none bg-muted/50 text-muted-foreground">
                            Ladder Top
                          </span>
                        </div>
                      )}

                      {sortedExtendedTiers.map((tier, idx) => (
                        <div
                          key={`method-control-ext-tier-${tier.sortOrder}-${tier.name}-${tier.rate}`}
                          className={cn(
                            'grid grid-cols-[56px_1fr_auto] items-center gap-2 px-3 py-2',
                            'rounded-lg border border-border/60 bg-surface-1 dark:bg-transparent'
                          )}
                        >
                          <span className="font-mono text-sm font-bold tabular-nums text-primary-text">
                            {tier.rate}x
                          </span>
                          <span className="font-heading text-[13px] uppercase tracking-wide truncate text-foreground">
                            {tier.name}
                          </span>
                          <span className="inline-flex items-center rounded-lg px-2 py-1 text-[11px] sm:text-[12px] font-mono font-semibold leading-none bg-primary/15 text-primary-text">
                            Ext {idx + 1}
                          </span>
                        </div>
                      ))}
                    </div>

                    {sortedExtendedTiers.length === 0 && (
                      <p className="text-xs font-mono text-muted-foreground italic text-center">
                        <span className="text-primary-text/70">tip:</span> add your first extended tier in the config form.
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </AnimatedCollapse>
        </>
      )}
    </div>
  );
}
